import {
    InvoiceScheduleBlockReason,
    getInvoiceScheduleBlockReason,
} from "@common/helpers/domains/accounting/invoiceScheduleEligibility";
import { isInvoiceItemLine } from "@common/helpers/lineItems";
import {
    InvoiceLineItemSchedule,
    InvoiceSchedule,
} from "@common/schemas/domains/invoice/invoice-response.schema";
import { MatchedSchedule } from "@common/schemas/domains/accounting/scheduleMatchedDisplay.schema";
import { InvoiceLineItemKind, InvoiceStatus } from "@common/types/invoice";
import { ScheduleApplicationStatus } from "@common/types/domains/accounting/schedule";
import { ScheduleCardValues } from "@components/accounting/schedules/scheduleCardCopy";

export type ScheduleCellState =
    | { kind: "add" }
    | {
          kind: "applied";
          scheduleName: string;
          scheduleApplicationId: string;
          // T5's drill-down drawer is keyed by template, not application.
          scheduleTemplateId: string | null;
          termEnd: string | null;
          remainingAmount: number;
          status: ScheduleApplicationStatus;
          viaLineItemCount?: number;
      }
    | { kind: "partial"; scheduledLineCount: number }
    // A line under a whole-invoice application: the schedule belongs to the
    // invoice, so the line shows it but changes happen on the invoice row.
    | { kind: "wholeInvoice"; schedule: ScheduleCardValues }
    // Unscheduled, but matched to transactions that carry the schedule.
    | { kind: "matched"; schedules: MatchedSchedule[] }
    | {
          kind: "unavailable";
          reason: ScheduleUnavailableReason | InvoiceScheduleBlockReason;
      };

const UNNAMED_SCHEDULE = "Schedule";

/**
 * A whole-invoice application covers every line. Both the Schedule column and
 * the Accrual ledger lock read this, so they cannot disagree about what
 * "scheduled" means — including for a `scope: "invoice"` row that somehow
 * carries no application id.
 */
function isWholeInvoiceScheduled(
    schedule: InvoiceSchedule | undefined,
): schedule is InvoiceSchedule & { scheduleApplicationId: string } {
    return (
        schedule?.scope === "invoice" && schedule.scheduleApplicationId !== null
    );
}

export enum ScheduleUnavailableReason {
    derivedLine = "derived_line",
    zeroAmount = "zero_amount",
}

function buildAppliedStateFromLineSchedule(
    lineSchedule: InvoiceLineItemSchedule,
    viaLineItemCount?: number,
): Extract<ScheduleCellState, { kind: "applied" }> {
    return {
        kind: "applied",
        scheduleName: lineSchedule.scheduleName ?? UNNAMED_SCHEDULE,
        scheduleApplicationId: lineSchedule.scheduleApplicationId,
        scheduleTemplateId: lineSchedule.scheduleTemplateId,
        termEnd: lineSchedule.termEnd,
        remainingAmount: lineSchedule.remainingAmount,
        status: lineSchedule.status,
        ...(viaLineItemCount !== undefined ? { viaLineItemCount } : {}),
    };
}

function resolvePartialLineScopedInvoiceSchedule(invoice: {
    lines?: Array<{
        kind: InvoiceLineItemKind;
        schedule?: InvoiceLineItemSchedule;
    }>;
    schedule?: InvoiceSchedule;
}): Extract<ScheduleCellState, { kind: "applied" }> | null {
    const schedule = invoice.schedule;
    if (schedule?.scope !== "lines" || !invoice.lines?.length) {
        return null;
    }
    const itemLines = invoice.lines.filter(isInvoiceItemLine);
    const scheduledLines = itemLines.filter((line) => line.schedule);
    if (scheduledLines.length === 0) {
        return null;
    }
    if (scheduledLines.length >= itemLines.length) {
        return null;
    }
    const applicationIds = new Set(
        scheduledLines.map(
            (line) => line.schedule?.scheduleApplicationId ?? "",
        ),
    );
    applicationIds.delete("");
    if (applicationIds.size !== 1) {
        return null;
    }
    const lineSchedule = scheduledLines[0].schedule;
    if (!lineSchedule) {
        return null;
    }
    return buildAppliedStateFromLineSchedule(
        lineSchedule,
        scheduledLines.length,
    );
}

function resolveUnifiedLineScopedInvoiceSchedule(invoice: {
    lines?: Array<{
        kind: InvoiceLineItemKind;
        schedule?: InvoiceLineItemSchedule;
    }>;
    schedule?: InvoiceSchedule;
}): Extract<ScheduleCellState, { kind: "applied" }> | null {
    const schedule = invoice.schedule;
    if (schedule?.scope !== "lines" || !invoice.lines?.length) {
        return null;
    }
    const itemLines = invoice.lines.filter(isInvoiceItemLine);
    const scheduledLines = itemLines.filter((line) => line.schedule);
    if (scheduledLines.length === 0) {
        return null;
    }
    const applicationIds = new Set(
        scheduledLines.map(
            (line) => line.schedule?.scheduleApplicationId ?? "",
        ),
    );
    applicationIds.delete("");
    if (applicationIds.size !== 1) {
        return null;
    }
    if (scheduledLines.length < itemLines.length) {
        return null;
    }
    const lineSchedule = scheduledLines[0].schedule;
    if (!lineSchedule) {
        return null;
    }
    return buildAppliedStateFromLineSchedule(
        lineSchedule,
        scheduledLines.length >= 2 ? scheduledLines.length : undefined,
    );
}

export function resolveInvoiceScheduleCellState(invoice: {
    status: InvoiceStatus;
    currency: string;
    total?: number;
    schedule?: InvoiceSchedule;
    matchedSchedules?: MatchedSchedule[];
    lines?: Array<{
        kind: InvoiceLineItemKind;
        schedule?: InvoiceLineItemSchedule;
    }>;
}): ScheduleCellState {
    const schedule = invoice.schedule;
    if (isWholeInvoiceScheduled(schedule)) {
        return {
            kind: "applied",
            // A nameless template would render an unlabelled button; name the
            // control rather than ship an empty one.
            scheduleName: schedule.scheduleName ?? UNNAMED_SCHEDULE,
            scheduleApplicationId: schedule.scheduleApplicationId,
            scheduleTemplateId: schedule.scheduleTemplateId,
            termEnd: schedule.termEnd,
            remainingAmount: schedule.remainingAmount ?? 0,
            status: schedule.status ?? ScheduleApplicationStatus.active,
        };
    }
    const unifiedLineSchedule =
        resolveUnifiedLineScopedInvoiceSchedule(invoice);
    if (unifiedLineSchedule) {
        return unifiedLineSchedule;
    }
    const partialLineSchedule =
        resolvePartialLineScopedInvoiceSchedule(invoice);
    if (partialLineSchedule) {
        return partialLineSchedule;
    }
    // "lines" scope with nothing actually scheduled would render a dead
    // "Partially scheduled (0 lines)" cell; treat it as unscheduled.
    if (schedule?.scope === "lines" && schedule.scheduledLineIds.length > 0) {
        return {
            kind: "partial",
            scheduledLineCount: schedule.scheduledLineIds.length,
        };
    }
    if (invoice.matchedSchedules?.length) {
        return { kind: "matched", schedules: invoice.matchedSchedules };
    }
    const blockReason = getInvoiceScheduleBlockReason(invoice);
    if (blockReason) {
        return { kind: "unavailable", reason: blockReason };
    }
    // Apply requires a positive amount; without this the user gets a generic
    // "failed to apply" toast from the backend rejection.
    if (invoice.total === 0) {
        return {
            kind: "unavailable",
            reason: ScheduleUnavailableReason.zeroAmount,
        };
    }
    return { kind: "add" };
}

export function resolveInvoiceLineScheduleCellState(line: {
    kind: InvoiceLineItemKind;
    amount?: number;
    schedule?: InvoiceLineItemSchedule;
    matchedSchedules?: MatchedSchedule[];
    invoice?: {
        status: InvoiceStatus;
        currency: string;
        schedule?: InvoiceSchedule;
    };
}): ScheduleCellState {
    if (line.schedule) {
        return {
            kind: "applied",
            scheduleName: line.schedule.scheduleName,
            scheduleApplicationId: line.schedule.scheduleApplicationId,
            scheduleTemplateId: line.schedule.scheduleTemplateId,
            termEnd: line.schedule.termEnd,
            remainingAmount: line.schedule.remainingAmount,
            status: line.schedule.status,
        };
    }
    // A whole-invoice application covers every line, but deliberately carries no
    // per-line badges (see buildState). Without this the lines would offer
    // "+ Add" and apply would 409 on the invoice-level conflict. The line shows
    // the invoice's card read-only: its "left" figure and any replace/remove
    // belong to the invoice, not to one line.
    const invoiceSchedule = line.invoice?.schedule;
    if (isWholeInvoiceScheduled(invoiceSchedule)) {
        return {
            kind: "wholeInvoice",
            schedule: {
                name: invoiceSchedule.scheduleName ?? UNNAMED_SCHEDULE,
                termEnd: invoiceSchedule.termEnd,
                remainingAmount: invoiceSchedule.remainingAmount ?? 0,
                status:
                    invoiceSchedule.status ?? ScheduleApplicationStatus.active,
            },
        };
    }
    if (line.matchedSchedules?.length) {
        return { kind: "matched", schedules: line.matchedSchedules };
    }
    // Tax and discount rows are synthetic splits of an item line. Sales tax is
    // a liability, not revenue, so deferring it into the holding account and
    // recognizing it would book tax collections as revenue.
    if (!isInvoiceItemLine(line)) {
        return {
            kind: "unavailable",
            reason: ScheduleUnavailableReason.derivedLine,
        };
    }
    const invoiceBlockReason = line.invoice
        ? getInvoiceScheduleBlockReason(line.invoice)
        : null;
    if (invoiceBlockReason) {
        return { kind: "unavailable", reason: invoiceBlockReason };
    }
    if (line.amount === 0) {
        return {
            kind: "unavailable",
            reason: ScheduleUnavailableReason.zeroAmount,
        };
    }
    return { kind: "add" };
}

export type InteractiveScheduleCellState = Extract<
    ScheduleCellState,
    { kind: "add" | "applied" }
>;

/** Both the empty "+ Add" and the applied card open the picker. */
export function isScheduleCellInteractive(
    state: ScheduleCellState,
): state is InteractiveScheduleCellState {
    return state.kind === "add" || state.kind === "applied";
}

export function toScheduleCardValues(
    state: InteractiveScheduleCellState,
): ScheduleCardValues | null {
    if (state.kind !== "applied") {
        return null;
    }
    return {
        name: state.scheduleName,
        termEnd: state.termEnd,
        remainingAmount: state.remainingAmount,
        status: state.status,
        ...(state.viaLineItemCount !== undefined
            ? { viaLineItemCount: state.viaLineItemCount }
            : {}),
    };
}

/**
 * Whether a schedule owns this line's accrual account — either the line itself
 * is scheduled, or the whole invoice is (in which case apply rewrote *every*
 * line's override, but carries no per-line badge).
 *
 * The Schedule column and the Accrual ledger lock both read this so they cannot
 * disagree about what "scheduled" means.
 */
export function isLineOwnedBySchedule(line: {
    schedule?: InvoiceLineItemSchedule;
    invoice?: { schedule?: InvoiceSchedule };
}): boolean {
    return (
        line.schedule !== undefined ||
        isWholeInvoiceScheduled(line.invoice?.schedule)
    );
}
